import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';
import { FaYoutube } from 'react-icons/fa';
import { Footer, ScrollToTop, TopNavigation } from './index';
import { SignupCtaSection } from '../sections';
import SubscriptionModal from './SubscriptionModal';
import HoverableQuestions from 'components/HoverableQuestions';
import NewsletterSignup from './NewsletterSignup';
import { show, setModalTimeout } from '../store/sub_modal';
import { populate } from '../store/hoverableQuestions';
import InfoBanner from './InfoBanner/Banner';
import { isProduction } from '@src/utils/env';
import { FloatingPanel } from './FloatingPanel';
import { AudioPlayer } from './Audio';
import ConditionalWrapper from './ConditionalWrapper';
import { useIsMobile } from '@src/hooks/useBreakpoints';
import { useQrTracking } from '@src/hooks/useQrTracking';
import ResourceSearchForm from './SearchForm';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import * as styles from './layout.module.css';

const isBrowser = typeof window !== 'undefined';

const LS_CLOSED_HOVERABLES = `closedHoverables-${new Date().toDateString()}`;

export const query = graphql`
  {
    subModal: allDatoCmsSubscriptionsModal {
      edges {
        node {
          backgroundUrl
          callToActionText
          content
          defaultOpenDelay
          mainButtons {
            link
            label
          }
          documentButtons {
            link
            label
          }
        }
      }
    }
    questions: allDatoCmsQuestionOfTheDay(filter: { active: { eq: true } }) {
      edges {
        node {
          fileName
          link
          shortDescription
          title
          originalId
          forceOpen
          hideFromSite
          file {
            url
          }
          fileUrl
          callToActionText
        }
      }
    }
    datoCmsSubscriptionPage {
      showAdsToSubscribedUsers
    }
    datoCmsGlobal {
      displayNewsletterSidebarPopout
      videoDisplay
      videoUrls
      topBanner {
        ...BannerFieldsFragment
      }
      bottomBanner {
        ...BannerFieldsFragment
      }
      audioFiles {
        id
        title
        fileUrl
        genres {
          label
        }
      }
    }
    genres: allDatoCmsGenre {
      edges {
        node {
          label
        }
      }
    }
  }
`;

const randomStart = (array) => {
  return array[Math.floor(Math.random() * array.length)];
};

const Layout = ({ children, location }) => {
  useQrTracking();
  const [pdfUrl, setPdfUrl] = useState(null);
  const user = useSelector((state) => state.user.data);
  const queryResult = useStaticQuery(query);
  const data = queryResult.subModal?.edges[0]?.node;
  const questions = queryResult.questions.edges;
  const showAdsToSubscribedUsers = queryResult?.datoCmsSubscriptionPage?.showAdsToSubscribedUsers;
  const showModal = useSelector((state) => state.subModal.show);
  const modalTimeout = useSelector((state) => state.subModal.timeout);
  const dispatch = useDispatch();
  const isSubscribed = user?.subscribed ?? false;

  const [timeoutId, setTimeoutId] = useState();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const isHomePage = location?.pathname === '/';

  const isQuestionBank = location?.pathname.includes('/question-bank');

  const isSearchPage =
    location?.pathname.includes('/search') || location?.pathname.includes('/question-bank');

  const isTopicPage = location?.pathname.includes('/topic');
  const isDocumentPreview = location?.pathname.includes('/maths/documents');

  const topBanner = queryResult?.datoCmsGlobal?.topBanner;
  const bottomBanner = queryResult?.datoCmsGlobal?.bottomBanner;
  const videoDisplay = queryResult?.datoCmsGlobal?.videoDisplay;

  const displayNewsletterSidebarPopout = queryResult?.datoCmsGlobal?.displayNewsletterSidebarPopout;
  const videoUrls = JSON.parse(queryResult?.datoCmsGlobal?.videoUrls) || [];
  const audioFiles = queryResult?.datoCmsGlobal?.audioFiles || [];
  const genres = queryResult?.genres?.edges?.map((edge) => edge.node) || [];

  useEffect(() => {
    if (queryResult?.datoCmsGlobal) {
      setIsDataLoaded(true);
    }
  }, [queryResult?.datoCmsGlobal]);

  React.useEffect(() => {
    const alreadyClosed = JSON.parse(localStorage.getItem(LS_CLOSED_HOVERABLES) ?? '[]');
    for (const key in localStorage) {
      if (key.substring(0, 16) === 'closedHoverables' && key !== LS_CLOSED_HOVERABLES) {
        localStorage.removeItem(key);
      }
    }
    const remainingQuestions = questions.filter(
      ({ node: question }) => !alreadyClosed.includes(question.originalId),
    );
    dispatch(populate(remainingQuestions));
  }, [questions]);

  useEffect(() => {
    if (isBrowser) {
      const openModal = localStorage.getItem('open-modal-on-load') ?? false;
      if (openModal) {
        if (!isSubscribed) dispatch(show(user));
        localStorage.removeItem('open-modal-on-load');
      }
    }
  }, []);
  useEffect(() => {
    if (timeoutId) clearTimeout(timeoutId);

    if (!modalTimeout) return;

    const newTimeoutId = setTimeout(() => {
      if (!isSubscribed) dispatch(show(user));
    }, modalTimeout * 1000);

    setTimeoutId(newTimeoutId);
  }, [modalTimeout, user]);

  useEffect(() => {
    if (!isSubscribed || showAdsToSubscribedUsers) return;

    const remove_ads = () => {
      const ads = [...document.querySelectorAll('.adsbygoogle')];
      ads.forEach((element) => element.remove());
      const ad_containers = [...document.querySelectorAll('.google-auto-placed')];
      ad_containers.forEach((el) => el.remove());
    };

    remove_ads();

    setTimeout(remove_ads, 3000);
  }, [isSubscribed]);

  useEffect(() => {
    const modalDelay = data?.defaultOpenDelay || 300;
    if (!modalTimeout) dispatch(setModalTimeout(modalDelay));
  }, [data]);

  const isMobile = useIsMobile();

  const [hasSeenFeedback, setHasSeenFeedback] = useLocalStorage('hasSeenWelcomeModal', false);
  const [hasVisitedBefore, setHasVisitedBefore] = useLocalStorage('hasVisitedBefore', false);
  const [tallyIsOpen, setTallyIsOpen] = useState(false);

  const openFeedbackModal = () => {
    if ('Tally' in window) {
      window.Tally.openPopup('q4R0xG', {
        layout: 'modal',
        autoClose: 1500,
        width: 700,
        onClose: () => {
          setTallyIsOpen(false);
        },
        onOpen: () => {
          setTallyIsOpen(true);
        },
      });
    }
  };

  useEffect(() => {
    if (!isBrowser || hasSeenFeedback) return;

    if (!hasVisitedBefore) {
      setHasVisitedBefore(true);
      return;
    }

    const t = setTimeout(() => {
      openFeedbackModal();
      setHasSeenFeedback(true);
    }, 2000);

    return () => clearTimeout(t);
  }, []);

  return (
    <div>
      {topBanner?.map((banner) => (
        <InfoBanner
          key={banner.label}
          banner={{
            ...banner,
            showNavigation: !isMobile,
          }}
          disableSticky={isTopicPage}
        />
      ))}

      <TopNavigation />

      {bottomBanner && !isHomePage && !isMobile && (
        <InfoBanner banner={bottomBanner} disableSticky={isTopicPage} />
      )}
      {isHomePage && <div className={styles.homeIndicator} />}

      <div
        id="custom-fuse-header"
        style={{
          height: isProduction ? '100px' : '0px',
        }}
      />

      <br />

      <HoverableQuestions />
      {displayNewsletterSidebarPopout && <NewsletterSignup />}

      {/* {!isSearchPage && <ResourceSearchForm />} */}

      {/* {audioFiles.length > 0 && !isMobile && (
        <AudioPlayer audioFiles={audioFiles} genres={genres} />
      )} */}

      {showModal && <SubscriptionModal data={data} />}
      {children}
      <SignupCtaSection />
      {!isMobile && videoUrls.length > 0 && isDataLoaded && videoDisplay !== 'none' && (
        <ConditionalWrapper
          condition={videoDisplay === 'panel-collapsed' || videoDisplay === 'panel-not-collapsed'}
          wrapper={(children) => (
            <FloatingPanel
              title="Maths Video Playlist"
              icon={<FaYoutube size={22} />}
              open={videoDisplay === 'panel-not-collapsed'}
            >
              {children}
            </FloatingPanel>
          )}
        >
          <div>
            <div className={`promo-video ${videoDisplay === 'floating' ? 'floating' : ''}`}>
              <ReactPlayer
                url={randomStart(videoUrls)}
                controls
                autoPlay
                muted
                playing
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </ConditionalWrapper>
      )}
      {!tallyIsOpen && !isQuestionBank && (
        <button
          style={{
            position: 'fixed',
            bottom: '10px',
            left: '10px',
            zIndex: 9999999999,
          }}
          className="button w-button extra-large-button"
          onClick={() => {
            if ('Tally' in window) {
              openFeedbackModal();
            }
          }}
        >
          Provide Feedback
        </button>
      )}
      <Footer
        audioPlayer={
          !isDocumentPreview &&
          audioFiles.length > 0 && <AudioPlayer audioFiles={audioFiles} genres={genres} />
        }
      />
      <ScrollToTop />
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

export { Layout };
